import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BUSINESS_MODEL_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
  CURRENCY_DISPLAY_MAP,
  getDisplayName,
} from '../constants/display-mappings';
import { formatSeekingInvestment } from '../utils/formatCompactAmount';
import { startupIndustryLabels } from '../utils/startupIndustryLabels';
import locationIcon from '../assets/icons/location.png';
import industryIcon from '../assets/icons/industry.png';
import bizModelIcon from '../assets/icons/biz_model.gif';
import stageIcon from '../assets/icons/stage.gif';
import amountIcon from '../assets/icons/amount.gif';
import eyeIcon from '../assets/eye.png';
import favoriteHeart from '../assets/favorites_icon__red_heart__right_red.png';
import { getSdgAlt, getSdgImage } from '../constants/sdgImages';

/** Max SDG icons on a catalog card so the row stays readable on small cards. */
const MAX_CARD_SDG_ICONS = 4;

interface StartupCardProps {
  startup: {
    id: string;
    companyName: string;
    thumbnailUrl?: string;
    country?: string;
    industry?: string;
    industries?: string[];
    businessModel?: string;
    stage?: string;
    investment?: number;
    currency?: string;
    selectedSDGs?: number[];
  };
  onClick: () => void;
  onMouseEnter?: () => void;
  /** When true, shows a "Previously viewed" badge on the thumbnail */
  isViewed?: boolean;
  /** When true, shows a "Following" badge (takes priority over Previously viewed) */
  isFavorited?: boolean;
  /** When true with isFavorited, shows only the heart icon (no "Following" label) */
  favoriteBadgeIconOnly?: boolean;
  /** When true, overlays the startup's SDG icons (SDG feed rows). */
  showSdgIcons?: boolean;
}

const StartupCard: React.FC<StartupCardProps> = ({
  startup,
  onClick,
  onMouseEnter,
  isViewed = false,
  isFavorited = false,
  favoriteBadgeIconOnly = false,
  showSdgIcons = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showBelow, setShowBelow] = useState(false);

  // Format investment amount
  const formatAmount = () => {
    if (!startup.investment) return 'N/A';
    const amount = startup.investment;
    const currency = startup.currency
      ? getDisplayName(CURRENCY_DISPLAY_MAP, startup.currency as any)
      : 'EUR';

    return formatSeekingInvestment(amount, currency);
  };

  const industryLabels = startupIndustryLabels(startup);

  const hasTooltipData =
    startup.country ||
    industryLabels.length > 0 ||
    startup.businessModel ||
    startup.stage ||
    startup.investment;

  // Calculate tooltip position when hovering; flip below card when too close to navbar
  // Listen to window scroll/resize to update position dynamically if parent scrolls while hovered
  useEffect(() => {
    if (!isHovered || !cardRef.current || !hasTooltipData) return;

    const updatePosition = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const NAVBAR_HEIGHT = 70;
      const phone = window.innerWidth < 1024;
      // On a phone the profile Follow button sits above the row, so the card opens under the thumbnail.
      const needsFlip = phone || rect.top < NAVBAR_HEIGHT + 160;
      setShowBelow(needsFlip);
      setTooltipPosition({
        top: needsFlip ? rect.bottom + 12 : rect.top - 12,
        left: rect.left + rect.width / 2,
      });
    };

    updatePosition();
    // Use capture phase to catch scroll events on any parent container
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered, hasTooltipData]);

  return (
    <>
      <div
        ref={cardRef}
        className="relative group"
        onMouseEnter={() => {
          setIsHovered(true);
          onMouseEnter?.();
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          onClick={onClick}
          className="relative cursor-pointer border border-[#444] rounded-sm overflow-hidden bg-[#111] transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-white/5"
          style={{ aspectRatio: '16 / 9' }}
        >
          {startup.thumbnailUrl ? (
            <img
              src={startup.thumbnailUrl}
              alt={startup.companyName}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#151515]">
              <div className="text-center p-4">
                <div className="text-4xl mb-2">🚀</div>
                <p className="text-white font-semibold text-xs sm:text-base line-clamp-2">
                  {startup.companyName}
                </p>
              </div>
            </div>
          )}

          {/* Name on hover, stacked above SDG icons so they never overlap */}
          <div className="absolute inset-x-0 bottom-0 z-[6] pointer-events-none flex flex-col justify-end">
            <div
              className={`bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                showSdgIcons &&
                Array.isArray(startup.selectedSDGs) &&
                startup.selectedSDGs.length > 0
                  ? 'px-3 pt-8 pb-1 md:px-4'
                  : 'p-3 md:p-4 pt-8'
              }`}
            >
              <p className="text-white font-semibold text-xs sm:text-base line-clamp-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                {startup.companyName}
              </p>
            </div>
            {showSdgIcons &&
              Array.isArray(startup.selectedSDGs) &&
              startup.selectedSDGs.length > 0 && (
                <div className="flex items-end gap-0.5 sm:gap-1 px-1.5 pb-1.5 sm:px-2 sm:pb-2">
                  {startup.selectedSDGs
                    .filter((n): n is number => typeof n === 'number' && n >= 1 && n <= 17)
                    .slice(0, MAX_CARD_SDG_ICONS)
                    .map(sdg => {
                      const src = getSdgImage(sdg);
                      if (!src) return null;
                      return (
                        <img
                          key={sdg}
                          src={src}
                          alt={getSdgAlt(sdg)}
                          title={getSdgAlt(sdg)}
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-[2px] shadow-md shadow-black/50 object-cover"
                        />
                      );
                    })}
                </div>
              )}
          </div>

          {/* Following takes priority over Previously viewed — same badge height */}
          {isFavorited ? (
            <div className="absolute top-0 right-0 pointer-events-none">
              <div
                className={`flex h-[22px] items-center bg-[#111] ${
                  favoriteBadgeIconOnly ? 'justify-center px-2' : 'gap-1 px-2.5'
                }`}
                style={{
                  borderBottomLeftRadius: '4px',
                  borderBottom: '1px solid rgba(239, 68, 68, 0.55)',
                  borderLeft: '1px solid rgba(239, 68, 68, 0.55)',
                  boxShadow: '-2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                <img
                  src={favoriteHeart}
                  alt=""
                  className={`shrink-0 object-contain ${
                    favoriteBadgeIconOnly ? 'h-3 w-3' : 'h-2.5 w-2.5'
                  }`}
                />
                {!favoriteBadgeIconOnly && (
                  <span className="text-gray-300 text-[10px] font-medium leading-none tracking-wide">
                    Following
                  </span>
                )}
              </div>
            </div>
          ) : (
            isViewed && (
              <div className="absolute top-0 right-0 pointer-events-none">
                <div
                  className="flex h-[22px] items-center gap-1 bg-[#111] px-2.5"
                  style={{
                    borderBottomLeftRadius: '4px',
                    borderBottom: '1px solid rgba(56, 189, 248, 0.4)',
                    borderLeft: '1px solid rgba(56, 189, 248, 0.4)',
                    boxShadow: '-2px 2px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  <img
                    src={eyeIcon}
                    alt=""
                    className="h-2.5 w-2.5 shrink-0 object-contain opacity-90"
                    aria-hidden
                  />
                  <span className="text-gray-300 text-[10px] font-medium leading-none tracking-wide">
                    Previously viewed
                  </span>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Tooltip on hover - using fixed positioning and portal to escape overflow containers */}
      {hasTooltipData &&
        isHovered &&
        createPortal(
          <div
            className="fixed opacity-100 pointer-events-none transition-opacity duration-300 z-[9999]"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: showBelow ? 'translate(-50%, 0%)' : 'translate(-50%, -100%)',
            }}
          >
            <div className="bg-[#3f3f3f] rounded-lg p-3 shadow-2xl min-w-[220px] border border-gray-700">
              {/* Arrow */}
              {showBelow ? (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-[-5px]">
                  <div className="w-3 h-3 bg-[#3f3f3f] border-l border-t border-gray-700 transform rotate-45"></div>
                </div>
              ) : (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="w-3 h-3 bg-[#3f3f3f] border-r border-b border-gray-700 transform rotate-45"></div>
                </div>
              )}

              <div className="space-y-2.5 relative z-10">
                {startup.country && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={locationIcon} alt="Location" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{startup.country}</span>
                  </div>
                )}

                {industryLabels.length > 0 && (
                  <div className="flex items-start gap-2.5 text-white text-sm">
                    <img
                      src={industryIcon}
                      alt="Industry"
                      className="mt-0.5 h-4 w-4 flex-shrink-0"
                    />
                    <div className="flex flex-col gap-0.5">
                      {industryLabels.map(label => (
                        <span key={label} className="whitespace-nowrap">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {startup.businessModel && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img
                      src={bizModelIcon}
                      alt="Business Model"
                      className="w-4 h-4 flex-shrink-0"
                    />
                    <span className="whitespace-nowrap">
                      {getDisplayName(BUSINESS_MODEL_DISPLAY_MAP, startup.businessModel as any)}
                    </span>
                  </div>
                )}

                {startup.stage && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={stageIcon} alt="Stage" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {getDisplayName(STAGE_DISPLAY_MAP, startup.stage as any)}
                    </span>
                  </div>
                )}

                {startup.investment && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={amountIcon} alt="Amount" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{formatAmount()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default React.memo(StartupCard, (prev, next) => {
  const prevSdgs = prev.startup.selectedSDGs?.join(',') ?? '';
  const nextSdgs = next.startup.selectedSDGs?.join(',') ?? '';
  return (
    prev.isViewed === next.isViewed &&
    prev.showSdgIcons === next.showSdgIcons &&
    prev.startup.id === next.startup.id &&
    prev.startup.thumbnailUrl === next.startup.thumbnailUrl &&
    prev.startup.companyName === next.startup.companyName &&
    prevSdgs === nextSdgs
  );
});
